import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { withSentryReactRouterV6Routing } from "@sentry/react";
import { LocaleContainer, SentryContrainer, VersionCheckContainer } from "~/containers";
import { routes } from "./configs/routes";

import { ABTestNewUICustomerQuestionnaire } from "./ABTestNewUICustomerQuestionnaire";
import { lazy, Suspense } from "react";
import { LoaderFullPage } from "./components";

const SentryRoutes = withSentryReactRouterV6Routing(Routes);

const Layout = lazy(
  () => import('./components/layout').then((module) => ({ default: module.Layout }))
);

const PartnerQuestionnairePage = lazy(() => import('./pages/questionnaires/v1/partner/ui/PartnerQuestionnairePage'));
const EmailSuccessPage = lazy(() => import('./pages/email-success/EmailSuccessPage'));
const EmailFailedPage = lazy(() => import('./pages/email-failed/EmailFailedPage'));
const MaintenancePage = lazy(() => import('./pages/maintenance/MaintenancePage'));
const SberIdCallbackPage = lazy(() => import('./pages/idp/SberIdCallbackPage'));

const PreserveQueryNavigate = ({ to }: { to: string }) => {
  const location = useLocation();
  const search = location.search;

  return <Navigate replace to={`${to}${search}`} />;
};


const isMaintenanceMode = import.meta.env.VITE_FEATURE_FLAG_is_enabled_maintenance_mode === "true";

export const App = (): JSX.Element => {
  if (isMaintenanceMode) {
    return (
      <SentryContrainer>
        <LocaleContainer>
          <BrowserRouter>
            <MaintenancePage />
          </BrowserRouter>
        </LocaleContainer>
      </SentryContrainer>
    );
  }

  return (
    <SentryContrainer>
      <LocaleContainer>
        <VersionCheckContainer>
          <BrowserRouter>
            <SentryRoutes>
              <Route
                path="/"
                element={<PreserveQueryNavigate to={routes.v2customer} />}
              />

              <Route
                path={routes.v2customer}
                element={
                  <ABTestNewUICustomerQuestionnaire />
                }
              />

              <Route
                path={routes.partner}
                element={
                  <Suspense fallback={<LoaderFullPage />}>
                    <Layout>
                      <PartnerQuestionnairePage />
                    </Layout>
                  </Suspense>
                }
              />

              <Route
                path={routes.emailSuccess}
                element={
                  <Suspense fallback={<LoaderFullPage />}>
                    <EmailSuccessPage />
                  </Suspense>
                }
              />

              <Route
                path={routes.emailFailed}
                element={
                  <Suspense fallback={<LoaderFullPage />}>
                    <EmailFailedPage />
                  </Suspense>
                }
              />

              <Route
                path={routes.sberIdCallback}
                element={
                  <Suspense fallback={<LoaderFullPage />}>
                    <SberIdCallbackPage />
                  </Suspense>
                }
              />

              <Route path="*" element={<Navigate to={routes.v2customer} replace />} />
            </SentryRoutes>
          </BrowserRouter>
        </VersionCheckContainer>
      </LocaleContainer>
    </SentryContrainer>
  );
};